import { useLocation, useParams } from 'react-router-dom'
import { useEffect, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Header, Footer, HeroSection, CTABanner } from '@/components/layout'
import { ChatBot } from '@/components/ChatBot'
import { localizedRoutes } from '@/i18n/routes'
import type { SupportedLanguage } from '@/i18n'

interface LayoutProps {
  children?: ReactNode
}

export function Layout({ children }: LayoutProps) {
  const { t, i18n } = useTranslation('common')
  const location = useLocation()
  const { lang } = useParams<{ lang: string }>()
  const currentLang = (lang || i18n.language) as SupportedLanguage

  // Determine current route from path
  const path = location.pathname
  const pathWithoutLang = path.replace(`/${currentLang}`, '').replace(/^\//, '')

  // Check if we're on the home page
  const isHome = pathWithoutLang === '' || pathWithoutLang === '/'

  // Get page key for hero content
  const getPageKey = (): string => {
    for (const [key, routes] of Object.entries(localizedRoutes)) {
      if (routes[currentLang as keyof typeof routes] === pathWithoutLang) {
        return key
      }
    }
    return 'home'
  }

  const pageKey = getPageKey()

  // Hero content based on page
  const getHeroContent = () => {
    switch (pageKey) {
      case 'home':
        return {
          title: t('home.hero.title'),
          subtitle: t('home.hero.subtitle'),
          cta: { text: t('home.hero.cta'), link: `/${currentLang}/${localizedRoutes.products[currentLang]}` },
          secondaryCta: { text: t('home.hero.ctaShop'), link: import.meta.env.VITE_ESHOP_URL || '/shop', external: true },
          variant: 'full' as const,
          backgroundImage: '/images/hero/hero-home.jpg',
        }
      case 'company':
        return {
          title: t('company.hero.title'),
          subtitle: t('company.hero.subtitle'),
          variant: 'compact' as const,
          backgroundImage: '/images/hero/hero-company.jpg',
        }
      case 'products':
        return {
          title: t('products.hero.title'),
          subtitle: t('products.hero.subtitle'),
          variant: 'compact' as const,
          backgroundImage: '/images/hero/hero-products.jpg',
        }
      case 'contacts':
        return {
          title: t('contacts.hero.title'),
          subtitle: t('contacts.hero.subtitle'),
          variant: 'compact' as const,
          backgroundImage: '/images/hero/hero-open-doors.png',
        }
      case 'sustainability':
        return {
          title: t('sustainability.hero.title'),
          subtitle: t('sustainability.hero.subtitle'),
          variant: 'compact' as const,
          backgroundImage: '/images/hero/hero-sustainability.png',
        }
      case 'openDoors':
        return {
          title: t('openDoors.hero.title'),
          subtitle: t('openDoors.hero.subtitle'),
          variant: 'compact' as const,
          backgroundImage: '/images/hero/hero-open-doors.png',
        }
      default:
        return {
          title: '',
          variant: 'compact' as const,
        }
    }
  }

  const heroContent = getHeroContent()

  // Update document title and meta description
  useEffect(() => {
    const metaKey = pageKey as string
    const pageTitle = t(`meta.${metaKey}.title`, { defaultValue: '' })
    const suffix = t('meta.titleSuffix')

    if (pageKey === 'home') {
      document.title = pageTitle || suffix
    } else {
      document.title = pageTitle ? `${pageTitle} | ${suffix}` : suffix
    }

    const pageDescription = t(`meta.${metaKey}.description`, { defaultValue: '' })
    if (pageDescription) {
      const metaDesc = document.querySelector('meta[name="description"]')
      if (metaDesc) metaDesc.setAttribute('content', pageDescription)
      const ogDesc = document.querySelector('meta[property="og:description"]')
      if (ogDesc) ogDesc.setAttribute('content', pageDescription)
    }

    const ogTitle = document.querySelector('meta[property="og:title"]')
    if (ogTitle) ogTitle.setAttribute('content', document.title)
  }, [pageKey, t])

  return (
    <div className="min-h-screen flex flex-col">
      <Header transparent={isHome} />

      <main className="flex-grow">
        {/* Hero Section */}
        {heroContent.title && (
          <HeroSection
            {...heroContent}
            overlay="dark"
            showBreadcrumb={!isHome}
          />
        )}

        {/* Page Content */}
        <div id="main-content">
          {children}
        </div>
      </main>

      {/* CTA Banner */}
      <CTABanner />

      <Footer />
      <ChatBot />
    </div>
  )
}
